/**
 * Per-Meta-ad-set detail route (#6210) — the Meta sibling of Google's ad-group page at
 * ``routes/.../google/$campaignslug/_slug/ad/$adId.tsx``.
 *
 * Same shell as the Meta ad route (sidebar left, detail panel right); the panel scopes to one ad
 * set — the tier Jeff's parity review named as the gap (Campaign → AD SET → Ad ≙ Google's
 * Campaign → Ad Group → Ad). The ``$metaCampaignSlug_`` segment opts out of nesting under the
 * campaign route file (which renders no <Outlet>), keeping ``/campaigns/meta/{slug}/adset/{id}``.
 */

import { useEffect } from 'react';
import { useSetAtom } from 'jotai';
import { createFileRoute, useParams } from '@tanstack/react-router';

import { campaignDataAtom, metaCampaignDataAtom } from '@/store';
import { CampaignSidebar, type PlatformFilter } from '@/components/campaign/campaign-sidebar';
import { CampaignSidebarToolbar } from '@/components/campaign/campaign-sidebar-toolbar';
import { MetaAdSetDetailPanel } from '@/components/campaign/meta/meta-ad-set-detail-panel';
import { TabCardPanel } from '@/components/glitch-ui/tab-card-panel';
import { useCampaignSidebarFilters } from '@/hooks/useCampaignSidebarFilters';

export const Route = createFileRoute('/_authenticated/_layout/campaigns/meta/$metaCampaignSlug_/adset/$adSetId')({
  component: MetaAdSetPage,
});

function MetaAdSetPage() {
  const { metaCampaignSlug, adSetId } = useParams({
    from: '/_authenticated/_layout/campaigns/meta/$metaCampaignSlug_/adset/$adSetId',
  });
  const setMetaCampaignData = useSetAtom(metaCampaignDataAtom);
  const setCampaignData = useSetAtom(campaignDataAtom);

  // Keep the campaign selected in the sidebar (and expanded to its ad sets) while an ad set is
  // open — the same anchoring the ad route does (#4660).
  useEffect(() => {
    setMetaCampaignData(metaCampaignSlug);
    setCampaignData(null);
  }, [metaCampaignSlug, setMetaCampaignData, setCampaignData]);

  const { filterTypes, filterStatuses, filterPlatforms, updateFilters } = useCampaignSidebarFilters();
  const platformFilter: PlatformFilter =
    filterPlatforms.size === 1 && filterPlatforms.has('google') ? 'google' :
    filterPlatforms.size === 1 && filterPlatforms.has('meta') ? 'meta' :
    'all';

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between py-0.5">
        <div className="flex items-center gap-3" />
      </div>
      <div className="flex-1">
        <div className="grid grid-cols-12 gap-1">
          <div className="col-span-3 sticky top-0 self-start h-[calc(100vh-120px)]">
            <TabCardPanel
              tabs={[{ id: 'all', label: 'Campaigns', isActive: true }]}
              actions={
                <CampaignSidebarToolbar
                  filterTypes={filterTypes}
                  filterStatuses={filterStatuses}
                  filterPlatforms={filterPlatforms}
                  showPlatforms
                  onFiltersChange={updateFilters}
                />
              }
              cardClassName="overflow-hidden"
              noPadding
            >
              <CampaignSidebar
                filterTypes={filterTypes}
                filterStatuses={filterStatuses}
                platformFilter={platformFilter}
              />
            </TabCardPanel>
          </div>
          <div className="col-span-9">
            <MetaAdSetDetailPanel slug={metaCampaignSlug} adSetId={Number(adSetId)} />
          </div>
        </div>
      </div>
    </div>
  );
}
